import axios from 'axios';
import { STORAGE_KEYS } from '../constants';

const DEBUG = import.meta.env.VITE_NODE_ENV !== 'production';

const debugLog = (...args) => {
  if (DEBUG) {
    console.log('[Axios Debug]', ...args);
  }
};

// Try to read API URL from environment or use fallback
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6000/api';
if (DEBUG) {
  console.log('🌐 API Base URL:', API_URL);
}

// Create a base axios instance for regular API calls
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable credentials for proper auth
  timeout: 30000, // Increased to 30 seconds for deployment
});

// Helper function to validate JWT token format
const isValidJWT = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
};

// Clear any malformed tokens on initialization
(() => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (token && !isValidJWT(token)) {
    console.warn('🧹 Clearing malformed token from localStorage on app initialization');
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }
})();

// Create a separate instance for file uploads
const apiUpload = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: true,
  timeout: 30000,
});

// Add request interceptor to both instances to handle auth tokens
const setupInterceptors = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      console.log('🔍 Request interceptor - Token from localStorage:', {
        exists: !!token,
        value: token,
        type: typeof token,
        length: token?.length,
        isString: typeof token === 'string',
        isNull: token === null,
        isStringNull: token === 'null'
      });

      if (token && token !== 'null') {
        // Validate token format before adding to headers
        if (isValidJWT(token)) {
          config.headers.Authorization = `Bearer ${token}`;
          debugLog('Added Authorization header:', `Bearer ${token.substring(0, 10)}...`);
        } else {
          console.error('❌ Invalid JWT token format detected, clearing auth state');
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          debugLog('WARNING: Malformed token removed from localStorage');
        }
      } else {
        debugLog('WARNING: No token found for request (or token is string "null")');
      }

      // Fix double 'api' in URLs - only if baseURL already contains /api
      if (config.url && config.url.startsWith('/api/') && config.baseURL && config.baseURL.includes('/api')) {
        config.url = config.url.replace('/api/', '/');
        debugLog('Fixed double API prefix in URL:', config.url);
      }

      // Special debug for worker update endpoints
      if (config.url?.includes('/update-worker') || config.url?.includes('/create-worker')) {
        console.log('🔍 Worker API Request:', {
          url: config.url,
          method: config.method,
          data: config.data ? JSON.parse(JSON.stringify(config.data)) : null
        });
      }

      // Log detailed request information (only in debug mode)
      if (DEBUG) {
        debugLog('Request Details:', {
          url: config.url,
          fullUrl: `${config.baseURL}${config.url}`,
          method: config.method,
          hasToken: !!token
        });
      }

      // Validate request payload for POST/PUT/PATCH
      if (['post', 'put', 'patch'].includes(config.method?.toLowerCase())) {
        if (config.data && typeof config.data === 'object') {
          debugLog('Request Payload Validation:', {
            hasData: !!config.data,
            dataType: typeof config.data,
            keys: Object.keys(config.data),
            qrCodeURL: config.data.qrCodeURL
          });
        }
      }

      // Log outgoing requests in development
      if (DEBUG) {
        console.log('🚀 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url
        });
      }

      return config;
    },
    (error) => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    }
  );
};

setupInterceptors(api);
setupInterceptors(apiUpload);

// Add a method for file uploads
api.upload = (url, formData, config = {}) => {
  return apiUpload.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (DEBUG) {
      debugLog('✅ API Response:', {
        status: response.status,
        url: response.config.url
      });
    }
    return response;
  },
  (error) => {
    // Enhanced error logging
    const fullUrl = error.config?.baseURL + error.config?.url;
    const status = error.response?.status;

    // Log errors appropriately
    if (DEBUG) {
      console.error('❌ API Error:', {
        status,
        url: error.config?.url,
        message: error.response?.data?.message || error.message
      });

      // Log server error details if available
      if (status === 500) {
        console.error('🔥 Server Error:', {
          endpoint: error.config?.url,
          error: error.response?.data?.error || error.message
        });
      }

      // Check for 404 errors
      if (status === 404) {
        console.error('🔍 404 Not Found:', {
          url: error.config?.url,
          fullUrl
        });
      }
    }

    // Handle token expiration - but don't redirect if on public pages
    if (status === 401 && !window.location.pathname.includes('/signin')) {
      debugLog('Unauthorized - clearing auth state');
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      // Use setTimeout to avoid navigation during render
      setTimeout(() => {
        window.location.href = '/signin';
      }, 100);
    }

    return Promise.reject(error);
  }
);

// Add a direct test method for debugging
api.testEndpoint = async (endpoint) => {
  try {
    console.log(`🔍 Testing endpoint: ${API_URL}${endpoint}`);
    const response = await axios.get(`${API_URL}${endpoint}`);
    console.log('✅ Test successful:', response.status, response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('❌ Test failed:', error.message);
    return { success: false, error: error.message };
  }
};

// Add a method to check the server status
api.checkServer = async () => {
  try {
    console.log('🔍 Checking server status...');
    const response = await axios.get(`${API_URL.split('/api')[0]}/health`);
    console.log('✅ Server is running:', response.status, response.data);
    return { online: true, data: response.data };
  } catch (error) {
    console.error('❌ Server may be offline:', error.message);
    return { online: false, error: error.message };
  }
};

// Add utility to fix worker passwords (only for development)
api.fixWorkerPasswords = async () => {
  try {
    console.log('🔧 Attempting to fix worker passwords...');
    const response = await api.post('/api/workers/fix-passwords');
    console.log('✅ Password fix complete:', response.data);
    return {
      success: true,
      message: response.data.message,
      workers: response.data.workers
    };
  } catch (error) {
    console.error('❌ Error fixing worker passwords:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      error
    };
  }
};

export default api; 